import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Phone, Clock, CreditCard } from 'lucide-react';
import type { CampingSpot } from '../../types';

type Meta = NonNullable<CampingSpot['commercialMetadata']>;

/**
 * Pris til visning. Brukerens/eierens `fee` (fritekst kr-beløp) vinner alltid.
 * Ellers Googles grove `priceLevel` (0–4) oversatt til en tekst-tier. null når
 * ingen av delene finnes → prischip skjules helt (ærlig dekning).
 */
export function priceDisplay(
  meta: Partial<Meta> | undefined | null,
  t: (key: string, fallback?: string) => string,
): string | null {
  // Presedens: brukerens/eierens eksakte `fee` > Googles faktiske `priceRange`
  // (kr-intervall) > Googles grove `priceLevel`-tier. null → prischip skjules.
  const fee = meta?.fee != null ? String(meta.fee).trim() : '';
  if (fee) return fee;
  const range = meta?.priceRange != null ? String(meta.priceRange).trim() : '';
  if (range) return range;
  const level = meta?.priceLevel;
  if (typeof level !== 'number') return null;
  if (level === 0) return t('campsite.price_free', 'Gratis');
  const clamped = Math.min(4, Math.max(1, level));
  const fallbacks: Record<number, string> = { 1: 'Rimelig', 2: 'Moderat', 3: 'Dyrt', 4: 'Svært dyrt' };
  return t(`campsite.price_level_${clamped}`, fallbacks[clamped]);
}

/**
 * Delt kontaktvisning (nettside, telefon, åpningstider, pris) for navngitte
 * plasser: kommersielle campingplasser/bobilplasser og attraksjons-POI-er.
 * Data hentes fra Google Places og lagres på `spot.commercialMetadata`.
 * Rendrer ingenting når ingen felt finnes.
 */
const ContactInfoBlock: React.FC<{ spot: CampingSpot }> = ({ spot }) => {
  const { t } = useTranslation();
  const meta = spot.commercialMetadata;
  const price = priceDisplay(meta, t as any);
  if (!meta?.website && !meta?.phone && !meta?.opening_hours && !price) return null;

  return (
    <div className="bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] p-3 flex flex-wrap gap-2 flex-shrink-0">
      {meta?.website && (
        <a
          href={meta.website.startsWith('http') ? meta.website : `https://${meta.website}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-blue-500/10 text-blue-400 text-2xs font-bold hover:bg-blue-500/20 transition-colors"
        >
          <Globe size={11} /> {t('campsite.website', 'Nettside')}
        </a>
      )}
      {meta?.phone && (
        <a
          href={`tel:${meta.phone}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-[var(--border-primary)] text-[var(--text-secondary)] text-2xs font-bold hover:bg-[var(--border-secondary)] transition-colors"
        >
          <Phone size={11} /> {meta.phone}
        </a>
      )}
      {price && (
        <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-[var(--border-primary)] text-[var(--text-secondary)] text-2xs font-bold">
          <CreditCard size={11} /> {price}
        </span>
      )}
      {meta?.opening_hours && (
        <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-[var(--border-primary)] text-[var(--text-secondary)] text-2xs font-bold whitespace-pre-line">
          <Clock size={11} /> {meta.opening_hours}
        </span>
      )}
    </div>
  );
};

export default ContactInfoBlock;
